import { useState, useEffect } from "react";
import Login from "./components/Login";
import BottomNavigation from "./components/BottomNavigation";
import ShipmentDetail from "./components/ShipmentDetail";
import CreateShipment from "./components/CreateShipment";
import ShipmentReport from "./components/ShipmentReport";
import { userStorage } from "./utils/storageUtils";
import { SCREENS, USER_TYPES } from "./constants";
import useNavigation from "./hooks/useNavigation";
import WeeklyCalendarView from "./components/WeeklyCalendarView";
import NotificationManager from "./components/NotificationManager";
import NotificationSystem from "./components/NotificationSystem";
import NotificationScreen from "./components/NotificationScreen";
import SettingsScreen from "./components/SettingsScreen";

const App = () => {
  const [userData, setUserData] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);

  // NEW: Notification states
  const [notificationCount, setNotificationCount] = useState(0);

  // Use navigation hook
  const {
    currentScreen,
    previousScreen,
    screenData,
    navigateTo,
    navigateToDetail,
    goBack,
    resetNavigation,
    canGoBack,
    _debug,
  } = useNavigation();

  useEffect(() => {
    // Check for saved user data on app start
    const savedUserData = userStorage.get();
    if (savedUserData) {
      setUserData(savedUserData);
      // If user is logged in but on login screen, navigate to appropriate screen based on user type
      if (currentScreen === SCREENS.LOGIN) {
        if (savedUserData.type === USER_TYPES.DISPATCHER) {
          navigateTo(SCREENS.REPORT, { clearStack: true });
        } else {
          navigateTo(SCREENS.CALENDAR, { clearStack: true });
        }
      }
    } else {
      // If no user data, ensure we're on login screen
      if (currentScreen !== SCREENS.LOGIN) {
        resetNavigation();
      }
    }
    setLoading(false);

    // Register service worker for PWA
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((err) => {
        console.log("Service worker registration failed:", err);
      });
    }
  }, [currentScreen, navigateTo, resetNavigation]);

  const handleLogin = (user) => {
    setUserData(user);

    // Navigate to appropriate screen based on user type
    if (user.type === USER_TYPES.DISPATCHER) {
      navigateTo(SCREENS.REPORT, { clearStack: true });
    } else {
      navigateTo(SCREENS.CALENDAR, { clearStack: true }); // Driver default to calendar
    }
  };

  // NEW: Handle notification received (mock function for now)
  const handleNotificationReceived = (notification) => {
    console.log("App received notification:", notification);

    // Increase notification count
    setNotificationCount((prev) => prev + 1);

    // Handle navigation if needed
    if (notification.action === "navigate" && notification.target) {
      // Use your navigation system to navigate to target
    }

    // Update app state if needed
    if (notification.data?.type === "shipment_assigned") {
      // Refresh shipments list
    }
  };

  const handleLogout = () => {
    userStorage.remove();
    setUserData(null);
    setSelectedShipment(null);
    setNotificationCount(0); // Reset notification count
    resetNavigation();
  };

  const handleSelectShipment = (shipment, sourceScreen = null) => {
    console.log(
      "Selecting shipment from screen:",
      sourceScreen || currentScreen
    );
    setSelectedShipment(shipment);
    navigateToDetail(
      { shipment, shipmentId: shipment.idChuyenXe || shipment.ID },
      sourceScreen
    );
  };

  const handleNavigate = (screen) => {
    console.log("Navigating to screen:", screen);
    setSelectedShipment(null);

    // Reset notification count when navigating to notification screen
    if (screen === SCREENS.NOTIFICATION) {
      setNotificationCount(0);
    }

    navigateTo(screen, { clearStack: true });
  };

  const handleBackFromDetail = () => {
    console.log("Going back from detail, can go back:", canGoBack);
    setSelectedShipment(null);
    goBack({
      defaultScreen:
        userData?.type === USER_TYPES.DISPATCHER
          ? SCREENS.REPORT
          : SCREENS.CALENDAR,
    });
  };

  const handleCreateShipment = () => {
    console.log("Navigating to create shipment screen");
    navigateTo(SCREENS.CREATE_SHIPMENT);
  };

  const handleBackFromCreate = () => {
    console.log("Going back from create shipment");
    goBack({
      defaultScreen:
        userData?.type === USER_TYPES.DISPATCHER
          ? SCREENS.REPORT
          : SCREENS.CALENDAR,
    });
  };

  const handleBackFromReport = () => {
    console.log("Going back from report");
    goBack({ defaultScreen: SCREENS.CALENDAR });
  };

  // Handle back from notification screen
  const handleBackFromNotification = () => {
    console.log("Going back from notification");
    goBack({
      defaultScreen:
        userData?.type === USER_TYPES.DISPATCHER
          ? SCREENS.REPORT
          : SCREENS.CALENDAR,
    });
  };

  // Handle back from settings screen
  const handleBackFromSettings = () => {
    console.log("Going back from settings");
    goBack({
      defaultScreen:
        userData?.type === USER_TYPES.DISPATCHER
          ? SCREENS.REPORT
          : SCREENS.CALENDAR,
    });
  };

  const handleSaveShipment = (shipmentData) => {
    console.log("Shipment saved:", shipmentData);
    // Optionally refresh the calendar or perform other actions
    // The create component already handles navigation back
  };

  // Get the shipment from screen data if available
  const currentShipment = selectedShipment || screenData.shipment;
  const detailSourceScreen = screenData.sourceScreen || previousScreen;

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Đang khởi tạo ứng dụng...</p>
        </div>
      </div>
    );
  }

  const handleNotificationClick = () => {
    setShowNotificationPanel(!showNotificationPanel);

    // Reset notification count when opening panel
    if (!showNotificationPanel) {
      setNotificationCount(0);
    }
  };
  const handleCloseNotificationPanel = () => {
    setShowNotificationPanel(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Panel Overlay */}
      {showNotificationPanel && userData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-md h-[70vh] rounded-t-2xl overflow-hidden">
            <NotificationManager
              userData={userData}
              onNotificationReceived={handleNotificationReceived}
              onClose={handleCloseNotificationPanel}
              isPanel={true} // NEW: Prop để biết đang ở panel mode
            />
          </div>
        </div>
      )}

      {/* Login Screen */}
      {currentScreen === SCREENS.LOGIN && <Login onLogin={handleLogin} />}

      {/* Calendar Screen */}
      {currentScreen === SCREENS.CALENDAR && userData && (
        <>
          {userData.type === USER_TYPES.DISPATCHER ? (
            <WeeklyCalendarView
              userData={userData}
              onSelectShipment={(shipment) =>
                handleSelectShipment(shipment, SCREENS.CALENDAR)
              }
              onCreateShipment={handleCreateShipment}
            />
          ) : (
            <WeeklyCalendarView
              userData={userData}
              onSelectShipment={(shipment) =>
                handleSelectShipment(shipment, SCREENS.CALENDAR)
              }
              onCreateShipment={handleCreateShipment}
            />
          )}

          <BottomNavigation
            activeScreen={SCREENS.CALENDAR}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            userData={userData}
            onNotificationClick={handleNotificationClick} // NEW
            notificationCount={notificationCount} // NEW
          />
        </>
      )}

      {/* Shipment Detail Screen */}
      {currentScreen === SCREENS.DETAIL &&
        (currentShipment || screenData.shipmentId) &&
        userData && (
          <ShipmentDetail
            shipmentId={
              screenData.shipmentId ||
              currentShipment?.idChuyenXe ||
              currentShipment?.ID
            }
            onBack={handleBackFromDetail}
            userData={userData}
            previousScreen={detailSourceScreen}
            canGoBack={canGoBack}
          />
        )}

      {/* Report Screen */}
      {currentScreen === SCREENS.REPORT && (
        <>
          <ShipmentReport
            userData={userData}
            onBack={handleBackFromReport}
            onShipmentClick={(shipmentId) => {
              console.log("Navigating to detail with shipmentId:", shipmentId);
              navigateToDetail({ shipmentId }, SCREENS.REPORT);
            }}
            onCreateShipment={handleCreateShipment}
          />
          <BottomNavigation
            activeScreen={SCREENS.REPORT}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            userData={userData}
            onNotificationClick={handleNotificationClick} // NEW
            notificationCount={notificationCount} // NEW
          />
        </>
      )}

      {currentScreen === SCREENS.NOTIFICATION && userData && (
        <>
          <NotificationScreen
            userData={userData}
            onBack={handleBackFromNotification}
          />
          <BottomNavigation
            activeScreen={SCREENS.NOTIFICATION}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            userData={userData}
            notificationCount={notificationCount}
          />
        </>
      )}

      {/* Settings Screen */}
      {currentScreen === SCREENS.SETTINGS && userData && (
        <>
          <SettingsScreen
            userData={userData}
            onBack={handleBackFromSettings}
            onLogout={handleLogout}
          />
          <BottomNavigation
            activeScreen={SCREENS.SETTINGS}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            userData={userData}
            notificationCount={notificationCount}
          />
        </>
      )}

      {/* Create Shipment Screen */}
      {currentScreen === SCREENS.CREATE_SHIPMENT &&
        userData &&
        userData.type === USER_TYPES.DISPATCHER && (
          <CreateShipment
            onBack={handleBackFromCreate}
            onSave={handleSaveShipment}
            userData={userData}
          />
        )}

      {/* Error Boundary */}
      {userData && !Object.values(SCREENS).includes(currentScreen) && (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Có lỗi xảy ra
            </h2>
            <p className="text-gray-600 mb-4">
              Không thể hiển thị màn hình được yêu cầu
            </p>
            <button
              onClick={() =>
                userData?.type === USER_TYPES.DISPATCHER
                  ? navigateTo(SCREENS.REPORT, { clearStack: true })
                  : navigateTo(SCREENS.CALENDAR, { clearStack: true })
              }
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Về trang chủ
            </button>
          </div>
        </div>
      )}

      {/* Notification System cho hiển thị thông báo in-app */}
      <NotificationSystem />
    </div>
  );
};

export default App;
